import {

  isRepairOrderDateAfterToday,

  isTireProductType,

  isVehicleTripIntakeComplete,

  type ClaimLaborLineRow,

  type ClaimPartLineRow,

  type ClaimRepairEntry,

} from './claimIntakeUtils';

import {

  isLaborLineNonEmpty,

  isPartLineNonEmpty,

  repairHasAtLeastOneLineItem,

} from './buildClaimGenerationPayload';

import { parseNumericField } from './buildUnifiedClaimPayload';
import {
  partsLaborWarrantyFieldError,
  type VehicleNotesFormState,
} from './vehicleNotesUtils';

const MSG_SELECT = 'Please select an option.';
const MSG_POSITIVE_NUMBER = 'Enter a valid positive number.';
const MSG_NON_NEGATIVE_NUMBER = 'Enter a valid number (0 or greater).';
const MSG_PHONE = 'Enter a valid phone number (at least 10 digits).';
const MSG_EMAIL = 'Enter a valid email address.';
const MSG_LINE_ITEMS =
  'Add at least one labor or part line with description and quantity or hours.';
const MSG_REPAIR_ORDER_DATE_FUTURE = 'Repair order date cannot be in the future.';
const MSG_POSTAL_CODE = 'Enter a numeric postal code with at least 5 digits.';

const FIELD_LABELS = {
  contractNumber: 'Contract Number',
  mileageIn: 'Mileage In',
  invoiceNumber: 'Invoice Number',
  repairOrderDate: 'Repair Order Date',
  customerName: 'Customer Name',
  customerPhone: 'Customer Phone',
  customerAddress1: 'Address Line 1',
  customerPostalCode: 'Postal Code',
  servicerName: 'Servicer Name',
  laborRate: 'Labor Rate ($/Hour)',
  servicerPostalCode: 'Postal Code',
  servicerAddress1: 'Address Line 1',
  servicerPhone: 'Servicer Phone Number',
  contactEmail: 'Contact Email',
  paymentEmail: 'Payment Email',
  complaint: 'Complaint',
  cause: 'Cause',
  correction: 'Correction',
  tiretread: 'Tire Tread',
  laborDescription: 'Description',
  laborHours: 'Hours',
  partDescription: 'Description',
  partQuantity: 'Qty',
  partUnitPrice: 'Unit Price',
} as const;

function requiredMessage(label: string): string {
  return `${label} is required.`;
}

export function sanitizePostalCodeInput(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** Strips non-numeric characters; allows at most one decimal point (e.g. labor rate, hours, qty, price). */
export function sanitizeDecimalInput(raw: string): string {
  let out = '';
  let hasDot = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
    } else if (ch === '.' && !hasDot) {
      out += ch;
      hasDot = true;
    }
  }
  return out;
}

export function validatePostalCode(value: string, label = FIELD_LABELS.customerPostalCode): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage(label);
  if (!/^\d+$/u.test(trimmed)) return MSG_POSTAL_CODE;
  if (trimmed.length < 5) return MSG_POSTAL_CODE;
  return undefined;
}



export type RepairsDocsScalarField =

  | 'arrivalMethod'

  | 'atServiceCenter'

  | 'warrantyMonths'

  | 'warrantyMiles'

  | 'reimbursement'

  | 'contractNumber'

  | 'mileageIn'

  | 'invoiceNumber'

  | 'repairOrderDate'

  | 'customerName'

  | 'customerPhone'

  | 'customerAddress1'

  | 'customerPostalCode'

  | 'servicerName'

  | 'laborRate'

  | 'servicerPostalCode'

  | 'servicerAddress1'

  | 'servicerPhone'

  | 'contactEmail'

  | 'paymentEmail';



export type LaborLineField = 'labor_description' | 'labor_hours';

export type PartLineField = 'part_description' | 'part_number' | 'part_quantity' | 'part_unit_price';



export type RepairsDocsFieldErrors = Partial<Record<RepairsDocsScalarField, string>>;



export type LaborLineFieldErrors = Partial<Record<LaborLineField, string>>;

export type PartLineFieldErrors = Partial<Record<PartLineField, string>>;



export type RepairFieldErrors = {

  complaint?: string;

  cause?: string;

  correction?: string;

  tiretread?: string;

  lineItemsSummary?: string;

  laborLines?: Partial<Record<number, LaborLineFieldErrors>>;

  partLines?: Partial<Record<number, PartLineFieldErrors>>;

};



export type RepairsDocsTabFormValues = Readonly<{

  arrivalMethod: string;

  atServiceCenter: string;

  reimbursement: string;

  paymentEmail: string;

  contractNumber: string;

  mileageIn: string;

  invoiceNumber: string;

  repairOrderDate: string;

  vehicleVin: string;

  customerName: string;

  customerPhone: string;

  customerAddress1: string;

  customerPostalCode: string;

  servicerName: string;

  laborRate: string;

  servicerPostalCode: string;

  servicerAddress1: string;

  servicerPhone: string;

  contactEmail: string;

  productType: string;

  hasGeneratedLineItems: boolean;

  repairs: ClaimRepairEntry[];

  vehicleNotes: VehicleNotesFormState;

}>;



export type RepairsDocsTabValidationResult = Readonly<{

  fields: RepairsDocsFieldErrors;

  repairs: Record<number, RepairFieldErrors>;

  vehicleVinMissing?: boolean;

  isValid: boolean;

}>;



function trimRequired(value: string, label: string): string | undefined {
  return value.trim() ? undefined : requiredMessage(label);
}

function validatePositiveNumber(value: string, label: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage(label);
  const n = parseNumericField(trimmed);
  if (n <= 0) return MSG_POSITIVE_NUMBER;
  return undefined;
}

function validateNonNegativeNumber(value: string, label: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage(label);
  const n = parseNumericField(trimmed);
  if (n < 0) return MSG_NON_NEGATIVE_NUMBER;
  return undefined;
}

function validatePhone(value: string, label: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return requiredMessage(label);
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 10) return MSG_PHONE;
  return undefined;
}



function validateOptionalEmail(value: string): string | undefined {

  const trimmed = value.trim();

  if (!trimmed) return undefined;

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(trimmed)) return MSG_EMAIL;

  return undefined;

}



function validateTripFields(values: RepairsDocsTabFormValues): RepairsDocsFieldErrors {

  const errors: RepairsDocsFieldErrors = {};

  if (!isVehicleTripIntakeComplete(values.arrivalMethod, values.atServiceCenter, values.reimbursement)) {

    if (values.arrivalMethod !== 'driven' && values.arrivalMethod !== 'towed') {
      errors.arrivalMethod = MSG_SELECT;
    }

    if (values.atServiceCenter !== 'yes' && values.atServiceCenter !== 'no') {
      errors.atServiceCenter = MSG_SELECT;
    }

    if (values.reimbursement !== 'Check' && values.reimbursement !== 'CONNEXPAY') {
      errors.reimbursement = MSG_SELECT;
    }

  }

  if (values.reimbursement === 'CONNEXPAY') {
    const paymentEmailTrimmed = values.paymentEmail.trim();
    if (!paymentEmailTrimmed) {
      errors.paymentEmail = requiredMessage(FIELD_LABELS.paymentEmail);
    } else {
      const paymentEmailErr = validateOptionalEmail(values.paymentEmail);
      if (paymentEmailErr) errors.paymentEmail = paymentEmailErr;
    }
  }

  return errors;

}



function validateInvoiceFields(values: RepairsDocsTabFormValues): RepairsDocsFieldErrors {

  const errors: RepairsDocsFieldErrors = {};

  const contractErr = trimRequired(values.contractNumber, FIELD_LABELS.contractNumber);
  if (contractErr) errors.contractNumber = contractErr;

  const mileageErr = validateNonNegativeNumber(values.mileageIn, FIELD_LABELS.mileageIn);
  if (mileageErr) errors.mileageIn = mileageErr;

  const invoiceErr = trimRequired(values.invoiceNumber, FIELD_LABELS.invoiceNumber);
  if (invoiceErr) errors.invoiceNumber = invoiceErr;

  const dateErr = trimRequired(values.repairOrderDate, FIELD_LABELS.repairOrderDate);
  if (dateErr) {
    errors.repairOrderDate = dateErr;
  } else if (isRepairOrderDateAfterToday(values.repairOrderDate)) {
    errors.repairOrderDate = MSG_REPAIR_ORDER_DATE_FUTURE;
  }

  const nameErr = trimRequired(values.customerName, FIELD_LABELS.customerName);
  if (nameErr) errors.customerName = nameErr;

  const phoneErr = validatePhone(values.customerPhone, FIELD_LABELS.customerPhone);
  if (phoneErr) errors.customerPhone = phoneErr;

  const addressErr = trimRequired(values.customerAddress1, FIELD_LABELS.customerAddress1);
  if (addressErr) errors.customerAddress1 = addressErr;

  const postalErr = validatePostalCode(values.customerPostalCode, FIELD_LABELS.customerPostalCode);
  if (postalErr) errors.customerPostalCode = postalErr;



  return errors;

}



function validateServiceFields(values: RepairsDocsTabFormValues): RepairsDocsFieldErrors {

  const errors: RepairsDocsFieldErrors = {};

  const nameErr = trimRequired(values.servicerName, FIELD_LABELS.servicerName);
  if (nameErr) errors.servicerName = nameErr;

  const rateErr = validatePositiveNumber(values.laborRate, FIELD_LABELS.laborRate);
  if (rateErr) errors.laborRate = rateErr;

  const postalErr = validatePostalCode(values.servicerPostalCode, FIELD_LABELS.servicerPostalCode);
  if (postalErr) errors.servicerPostalCode = postalErr;

  const addressErr = trimRequired(values.servicerAddress1, FIELD_LABELS.servicerAddress1);
  if (addressErr) errors.servicerAddress1 = addressErr;

  const phoneErr = validatePhone(values.servicerPhone, FIELD_LABELS.servicerPhone);
  if (phoneErr) errors.servicerPhone = phoneErr;

  const contactEmailTrimmed = values.contactEmail.trim();
  if (!contactEmailTrimmed) {
    errors.contactEmail = requiredMessage(FIELD_LABELS.contactEmail);
  } else {
    const emailErr = validateOptionalEmail(values.contactEmail);
    if (emailErr) errors.contactEmail = emailErr;
  }



  return errors;

}



function validateRepairThreeCs(repair: ClaimRepairEntry): RepairFieldErrors {

  const errors: RepairFieldErrors = {};

  const complaintErr = trimRequired(repair.complaint, FIELD_LABELS.complaint);
  if (complaintErr) errors.complaint = complaintErr;

  const causeErr = trimRequired(repair.cause, FIELD_LABELS.cause);
  if (causeErr) errors.cause = causeErr;

  const correctionErr = trimRequired(repair.correction, FIELD_LABELS.correction);
  if (correctionErr) errors.correction = correctionErr;

  return errors;

}



function validateLaborLineFields(line: ClaimLaborLineRow): LaborLineFieldErrors {

  const errors: LaborLineFieldErrors = {};

  if (!line.labor_description.trim()) {
    errors.labor_description = requiredMessage(FIELD_LABELS.laborDescription);
  }

  if (!line.labor_hours.trim()) {
    errors.labor_hours = requiredMessage(FIELD_LABELS.laborHours);
  } else if (parseNumericField(line.labor_hours) <= 0) {

    errors.labor_hours = 'Labor hours must be greater than zero.';

  }

  return errors;

}



function validatePartLineFields(line: ClaimPartLineRow): PartLineFieldErrors {

  const errors: PartLineFieldErrors = {};

  if (!line.part_description.trim()) {
    errors.part_description = requiredMessage(FIELD_LABELS.partDescription);
  }

  if (!line.part_quantity.trim()) {
    errors.part_quantity = requiredMessage(FIELD_LABELS.partQuantity);
  } else if (parseNumericField(line.part_quantity) <= 0) {

    errors.part_quantity = 'Part quantity must be greater than zero.';

  }

  if (!line.part_unit_price.trim()) {
    errors.part_unit_price = requiredMessage(FIELD_LABELS.partUnitPrice);
  } else if (parseNumericField(line.part_unit_price) < 0) {

    errors.part_unit_price = 'Part unit price cannot be negative.';

  }

  return errors;

}



function validateRepairTireTread(repair: ClaimRepairEntry): Pick<RepairFieldErrors, 'tiretread'> {

  const trimmed = repair.tiretread.trim();

  if (!trimmed) return { tiretread: requiredMessage(FIELD_LABELS.tiretread) };

  const n = parseNumericField(trimmed);

  if (!Number.isInteger(n) || n < 0 || n > 32) {

    return { tiretread: 'Enter a whole number from 0 to 32.' };

  }

  return {};

}



function validateRepairLineItems(repair: ClaimRepairEntry): Pick<RepairFieldErrors, 'lineItemsSummary' | 'laborLines' | 'partLines'> {

  const out: Pick<RepairFieldErrors, 'lineItemsSummary' | 'laborLines' | 'partLines'> = {};



  if (!repairHasAtLeastOneLineItem(repair)) {

    out.lineItemsSummary = MSG_LINE_ITEMS;

    return out;

  }



  const laborLines: Record<number, LaborLineFieldErrors> = {};

  repair.labor_lines.forEach((line, lineIdx) => {

    if (!isLaborLineNonEmpty(line)) return;

    const lineErrors = validateLaborLineFields(line);

    if (Object.keys(lineErrors).length > 0) laborLines[lineIdx] = lineErrors;

  });

  if (Object.keys(laborLines).length > 0) out.laborLines = laborLines;



  const partLines: Record<number, PartLineFieldErrors> = {};

  repair.part_lines.forEach((line, lineIdx) => {

    if (!isPartLineNonEmpty(line)) return;

    const lineErrors = validatePartLineFields(line);

    if (Object.keys(lineErrors).length > 0) partLines[lineIdx] = lineErrors;

  });

  if (Object.keys(partLines).length > 0) out.partLines = partLines;



  return out;

}



function validateRepairEntry(repair: ClaimRepairEntry, productType: string): RepairFieldErrors {

  const errors: RepairFieldErrors = {

    ...validateRepairThreeCs(repair),

    ...(isTireProductType(productType) ? validateRepairTireTread(repair) : {}),

    ...validateRepairLineItems(repair),

  };

  return errors;

}



function repairErrorsAreEmpty(errors: RepairFieldErrors): boolean {

  if (errors.complaint || errors.cause || errors.correction || errors.tiretread || errors.lineItemsSummary) return false;

  if (errors.laborLines && Object.keys(errors.laborLines).length > 0) return false;

  if (errors.partLines && Object.keys(errors.partLines).length > 0) return false;

  return true;

}



function validatePartsLaborWarrantyFields(values: RepairsDocsTabFormValues): RepairsDocsFieldErrors {
  const errors: RepairsDocsFieldErrors = {};
  const monthsErr = partsLaborWarrantyFieldError(values.vehicleNotes.partsLaborWarrantyMonths, 'months');
  if (monthsErr) errors.warrantyMonths = monthsErr;
  const milesErr = partsLaborWarrantyFieldError(values.vehicleNotes.partsLaborWarrantyMiles, 'miles');
  if (milesErr) errors.warrantyMiles = milesErr;
  return errors;
}

/** Field-level validation for Repairs & Docs — mirrors generate/submit readiness rules. */

export function validateRepairsDocsTab(values: RepairsDocsTabFormValues): RepairsDocsTabValidationResult {

  const fields: RepairsDocsFieldErrors = {

    ...validateTripFields(values),

    ...validateInvoiceFields(values),

    ...validateServiceFields(values),

    ...validatePartsLaborWarrantyFields(values),

  };



  const repairs: Record<number, RepairFieldErrors> = {};

  values.repairs.forEach((repair, idx) => {

    const repairErrors = validateRepairEntry(repair, values.productType);

    if (!repairErrorsAreEmpty(repairErrors)) {

      repairs[idx] = repairErrors;

    }

  });



  const vehicleVinMissing = !values.vehicleVin.trim();

  const isValid =

    Object.keys(fields).length === 0 &&

    Object.keys(repairs).length === 0 &&

    !vehicleVinMissing;

  return {

    fields,

    repairs,

    ...(vehicleVinMissing ? { vehicleVinMissing: true } : {}),

    isValid,

  };

}



/** First repair index with a validation error (for scroll-into-view). */
export function firstRepairIndexWithErrors(
  repairs: Record<number, RepairFieldErrors>
): number | undefined {
  const keys = Object.keys(repairs);
  if (keys.length === 0) return undefined;
  return Number(keys.sort((a, b) => Number(a) - Number(b))[0]);
}

const SCALAR_FIELD_SCROLL_ORDER: readonly RepairsDocsScalarField[] = [
  'arrivalMethod',
  'atServiceCenter',
  'warrantyMonths',
  'warrantyMiles',
  'reimbursement',
  'paymentEmail',
  'contractNumber',
  'mileageIn',
  'invoiceNumber',
  'repairOrderDate',
  'customerName',
  'customerPhone',
  'customerAddress1',
  'customerPostalCode',
  'servicerName',
  'laborRate',
  'servicerPostalCode',
  'servicerPhone',
  'contactEmail',
  'servicerAddress1',
] as const;

/** DOM id for a scalar field used by scroll-into-view on validation failure. */
export function repairsDocsFieldScrollId(field: RepairsDocsScalarField): string {
  return `repairs-field-${field}`;
}

/** First element id to scroll to when validation fails (scalar fields before repair cards). */
export function firstRepairsDocsErrorScrollId(
  fields: RepairsDocsFieldErrors,
  repairs: Record<number, RepairFieldErrors>
): string | null {
  for (const field of SCALAR_FIELD_SCROLL_ORDER) {
    if (fields[field]) return repairsDocsFieldScrollId(field);
  }
  const repairIdx = firstRepairIndexWithErrors(repairs);
  if (repairIdx !== undefined) return `repair-card-${repairIdx}`;
  return null;
}



export function isRepairsDocsInvoiceMetaComplete(values: Pick<

  RepairsDocsTabFormValues,

  | 'mileageIn'

  | 'invoiceNumber'

  | 'repairOrderDate'

  | 'vehicleVin'

  | 'customerName'

  | 'customerPhone'

  | 'customerAddress1'

  | 'customerPostalCode'

  | 'contractNumber'

>): boolean {

  return Boolean(

    values.mileageIn.trim() &&

      values.invoiceNumber.trim() &&

      values.vehicleVin.trim() &&

      values.repairOrderDate.trim() &&
      !isRepairOrderDateAfterToday(values.repairOrderDate) &&

      values.customerName.trim() &&

      values.customerPhone.trim() &&

      values.customerAddress1.trim() &&

      values.customerPostalCode.trim() &&
      !validatePostalCode(values.customerPostalCode) &&

      values.contractNumber.trim()

  );

}



export function isRepairsDocsServiceInfoComplete(values: Pick<

  RepairsDocsTabFormValues,

  'servicerName' | 'laborRate' | 'servicerPostalCode' | 'servicerAddress1' | 'servicerPhone' | 'hasGeneratedLineItems'

>): boolean {

  const baseComplete = Boolean(

    values.servicerName.trim() &&

      values.laborRate.trim() &&

      values.servicerPostalCode.trim() &&

      !validatePositiveNumber(values.laborRate, FIELD_LABELS.laborRate) &&

      !validatePostalCode(values.servicerPostalCode)

  );

  if (!baseComplete) return false;

  if (!values.servicerAddress1.trim()) return false;

  if (validatePhone(values.servicerPhone, FIELD_LABELS.servicerPhone)) return false;

  return true;

}


